/* SIZESOFTX
=========================================================================================
* Descripción: Modal para buscar clientes
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 15/04/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 15/04/2026 | Lic. José Luis Aragón Cervantes  | Mejora título y navegación con flechas
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Apertura de modal sin animación
*      4       | 10/10/2026 | Lic. José Luis Aragón C.  | Push-to-talk: el micrófono se escucha mientras se mantiene presionado y entrega el texto al soltar
*/

import TextField from "../ui/TextField";
import React, { Fragment, useState, useEffect, useRef } from "react";
import { Modal, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { apiClientesBusquedaFiltro } from "../../settings/ApiConfig";
import SearchIcon from "@mui/icons-material/Search";

import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import SizeSoftXTablePaginadoFilter from "../leyout/SizeSoftXTablePaginadoFilter";
import useSpeechToText from "../leyout/useSpeechToText";
import BotonVoz from "../leyout/BotonVoz";

const modalStyle = {
    position: 'fixed',
    top: '6%',
    right: '9%',
    width: '81vw',
    height: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '9px',
    zoom: '90%',
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0
};

const rowsPerPage = 10;

export default function ModalBuscarClientes(props) {
    const { openModal, handleCloseModal, onClienteSelect } = props;

    const filtroRef = useRef(null);
    const tablaRef = useRef(null);

    const { GetRequest } = useRequest();
    const { handleOpenAlert } = useLayout();

    const { isSupported, hasPermission, isListening, error: speechError, startListening, stopListening, checkPermission } = useSpeechToText({
        onResult: (text) => {
            const cleaned = text.replace(/[.]+$/, '').trim();
            setDataFiltro(cleaned);
            HandleBuscarClientesPaginado(cleaned, 0);
            setTimeout(() => filtroRef.current?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })), 100);
        }
    });

    const [listaClientesFiltro, setListaClientesFiltro] = useState([]);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [clienteSeleccionado, setClienteSeleccionado] = useState({});
    const [dataFiltro, setDataFiltro] = useState("");

    const [page, setPage] = useState(0);

    useEffect(() => {
        if (openModal) {
            setClienteSeleccionado({});
            setPage(0);
            setTimeout(() => {
                filtroRef.current?.focus();
                filtroRef.current?.select();
            }, 0);
        }
    }, [openModal]);

    useEffect(() => {
        if (openModal) checkPermission();
    }, [openModal, checkPermission]);

    const handleChangeFilter = (e) => {
        setDataFiltro(e.target.value);
    };

    const HandleBuscarClientesPaginado = async (filtro, pageIndex = 0) => {
        const inicial = pageIndex * rowsPerPage;
        const totalPorPagina = rowsPerPage;

        let url = apiClientesBusquedaFiltro
            .replace("{busqueda}", encodeURIComponent(filtro))
            .replace("{registroinicial}", inicial)
            .replace("{totalregistros}", totalPorPagina);

        const response = await GetRequest({
            url,
            loader: "Buscando clientes...",
        });

        if (response.hasError) {
            handleOpenAlert(response.message);
            return { lista: [], total: 0 };
        }

        const lista = response.result.listaclientes ?? [];
        const total = response.result.totalregistros ?? 0;

        setListaClientesFiltro(lista);
        setTotalRegistros(total);
        setPage(pageIndex);

        return { lista, total };
    };

    const handleOnKeyDownFilter = async (e) => {
        try {
            if (e.key === 'Enter') {
                if (dataFiltro.trim().length === 0) {
                    setListaClientesFiltro([]);
                    setTotalRegistros(0);
                    return;
                }

                await HandleBuscarClientesPaginado(dataFiltro.trim(), 0);
                return;
            }
            else if (e.key === 'ArrowDown') {
                if (listaClientesFiltro.length === 0) return;
                tablaRef.current?.focus();
                setClienteSeleccionado(listaClientesFiltro[0] || {});
            }
        }
        catch {
            return;
        }
    };

    const handleChangePartidaSeleccionada = (row) => {
        if (row) {
            setClienteSeleccionado(row);
        }
    };

    const handleDoubleClickRow = (row) => {
        if (row && row.clavecliente) {
            setClienteSeleccionado(row);
            onClienteSelect(row);
            handleClose();
        }
    };

    const handleCatchKeyTable = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (clienteSeleccionado && clienteSeleccionado.clavecliente) {
                onClienteSelect(clienteSeleccionado);
                handleClose();
            }
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            handleClose();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const currentIndex = listaClientesFiltro.findIndex(r => r.clavecliente === clienteSeleccionado?.clavecliente);
            if (currentIndex < listaClientesFiltro.length - 1) {
                setClienteSeleccionado(listaClientesFiltro[currentIndex + 1]);
            } else if (page < Math.ceil(totalRegistros / rowsPerPage) - 1) {
                const nextPage = page + 1;
                setPage(nextPage);
                HandleBuscarClientesPaginado(dataFiltro, nextPage);
            }
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            const currentIndex = listaClientesFiltro.findIndex(r => r.clavecliente === clienteSeleccionado?.clavecliente);
            if (currentIndex > 0) {
                setClienteSeleccionado(listaClientesFiltro[currentIndex - 1]);
            } else if (page > 0) {
                const prevPage = page - 1;
                setPage(prevPage);
                HandleBuscarClientesPaginado(dataFiltro, prevPage);
            }
            return;
        }
    };

    const handleClose = () => {
        if (props.handleCloseModal) {
            props.handleCloseModal();
        }
    };

    const columns = [
        { title: "Clave", field: "clavecliente", width: 100 },
        { title: "Razón Social", field: "razonsocial", width: 250 },
        { title: "RFC", field: "rfc", width: 120 },
        { title: "Teléfono", field: "telefono1", width: 120 },
        { title: "Email", field: "email", width: 180 },
        { title: "Ciudad", field: "ciudadpais", width: 150 },
    ];

    return (
        <Modal
            open={openModal}
            onClose={handleClose}
            aria-labelledby="modal-buscar-clientes-title"
            aria-describedby="modal-buscar-clientes-description"
        >
            <Box sx={modalStyle}>
                <Fragment>
                    <Box sx={{ backgroundColor: '#23165d', padding: '10px', borderRadius: '4px 4px 0 0' }}>
                        <Typography variant="h6" sx={{ color: 'white', margin: 0, fontWeight: 'bold' }}>
                            Buscador de Clientes
                        </Typography>
                    </Box>
                    <div className="filtrobuscadorarticulos" style={{ padding: '10px', backgroundColor: '#f5f5f5' }}>
                         <TextField
                             inputRef={filtroRef}
                             name="Filtro"
                             label="Buscar por: Clave, Razón Social, RFC:"
                             value={dataFiltro}
                             onChange={handleChangeFilter}
                             onKeyDown={handleOnKeyDownFilter}
                             type="text"
                             inputProps={{ maxLength: 100 }}
                             sx={{ width: '100%' }}
                             size="small"
                             autoComplete="off"
                         />
                         <BotonVoz
                                 isSupported={isSupported}
                                 hasPermission={hasPermission}
                                 isListening={isListening}
                                 onStart={startListening} onStop={stopListening}
                             />
                     </div>
                    <div className="tablaarticulosfiltrados" style={{ flex: 1, overflow: 'auto' }}>
                        <div
                            tabIndex={0}
                            ref={tablaRef}
                            onKeyDown={handleCatchKeyTable}
                            style={{ outline: 'none', height: '100%' }}
                        >
                            <SizeSoftXTablePaginadoFilter
                                columns={columns}
                                data={listaClientesFiltro}
                                totalDeRegistros={totalRegistros}
                                id="clavecliente"
                                row={clienteSeleccionado}
                                setRow={handleChangePartidaSeleccionada}
                                fontSize="small"
                                rowsPerPageSet={rowsPerPage}
                                pageNow={page}
                                onRowDoubleClick={handleDoubleClickRow}
                            />
                        </div>
                    </div>
                    <div style={{ padding: '10px', textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: '0 0 4px 4px' }}>
                        <TextField autoComplete="off"
                            label="Cliente seleccionado"
                            value={clienteSeleccionado.razonsocial || ""}
                            disabled
                            size="small"
                            sx={{ width: '60%', mr: 1 }}
                        />
                        <Tooltip title="Seleccionar cliente (Enter)">
                            <IconButton
                                onClick={() => {
                                    if (clienteSeleccionado && clienteSeleccionado.clavecliente) {
                                        onClienteSelect(clienteSeleccionado);
                                        handleClose();
                                    }
                                }}
                                disabled={!clienteSeleccionado.clavecliente}
                                color="primary"
                                sx={{ backgroundColor: '#23165d', color: 'white', '&:hover': { backgroundColor: '#1a103f' } }}
                            >
                                <SearchIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </Fragment>
            </Box>
        </Modal>
    );
}


